import React, { useEffect, useRef, useState } from "react";
import "../../pages/home/home.scss";
import "./appointment.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faClipboardCheck,
  faEllipsisVertical,
  faEye,
  faUserGroup,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import CustomModal from "../../components/common/modal/modal";
import CreateAppointment from "../../components/appointment/create-appointment";
import { appointmentStatusChange, createVisit } from "../../service/api/appointment-api";
import { getDoctorTodaySummary } from "../../store/slices/dashboardSlice";
import { getWithParams } from "../../utils/fetchAPI";
import {
  formatDate,
  formatOfficeLocationLine,
  resolveActingDoctorId,
} from "../../utils/common-funtions/common";
import { DashboardStatCardsSkeleton } from "./dashboard-skeleton";

function todayStr() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const initials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
};

const avatarPalette = ["#0fac81", "#0573f5", "#5b6cf3", "#e35d8a", "#e8902c", "#8957e5"];
const avatarColor = (name) => {
  if (!name) return avatarPalette[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash + name.charCodeAt(i)) % avatarPalette.length;
  return avatarPalette[hash];
};

const titleCase = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "—");

const AssistantTodayConsole = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, selectedRole, selectedDoctor } = useSelector((state) => state.auth);
  const { todaySummary, isTodaySummaryLoading } = useSelector((state) => state.dashboard);

  const actingDoctorId = resolveActingDoctorId({ user, selectedRole, selectedDoctor });
  const parentDoctors = Array.isArray(selectedRole?.parentUsers) ? selectedRole.parentUsers : [];

  const [today] = useState(todayStr);
  const [show, setShow] = useState(false);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const ref = useRef([]);

  const fetchTodayList = async () => {
    if (!selectedRole?._id || !actingDoctorId) {
      setTodayAppointments([]);
      return;
    }
    setIsListLoading(true);
    try {
      const res = await getWithParams("/appointment/list", {
        userRoleId: selectedRole._id,
        doctorId: actingDoctorId,
        startDate: today,
        endDate: today,
        per_page: 100,
        current_page: 1,
      });
      const list = res?.data?.appointments || [];
      list.sort((a, b) => (a?.serial ?? 0) - (b?.serial ?? 0));
      setTodayAppointments(list);
    } catch (error) {
      setTodayAppointments([]);
    } finally {
      setIsListLoading(false);
    }
  };

  const refreshAll = () => {
    if (!selectedRole?._id || !actingDoctorId) return;
    dispatch(
      getDoctorTodaySummary({ userRoleId: selectedRole._id, doctorId: actingDoctorId, date: today })
    );
    fetchTodayList();
  };

  // Refetch when the active doctor or chamber changes.
  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole?._id, selectedDoctor?._id]);

  // Close the status menu on outside click.
  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (selectedRow && ref.current[selectedRow] && !ref.current[selectedRow].contains(e.target)) {
        setSelectedRow(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => document.removeEventListener("mousedown", checkIfClickedOutside);
  }, [selectedRow]);

  const setStatusFun = async (status, info) => {
    await appointmentStatusChange(info?._id, { status: status?.toLowerCase() })
      .then((res) => {
        if (res.status === 200) {
          setSelectedRow();
          refreshAll();
        }
      })
      .catch((error) => console.log(error));
  };

  const createVisitAndNavigate = async (appointment) => {
    await createVisit(appointment?._id)
      .then((res) => {
        if (res.status === 200) navigate(`/appointment/${res?.data?._id}`);
      })
      .catch((error) => console.log(error));
  };

  const s = todaySummary || {};
  const tiles = [
    {
      label: "Today's appointments",
      value: s.total ?? "—",
      hint: "All of this doctor's appointments today at this chamber",
      icon: faCalendarDays,
      variant: "teal",
    },
    {
      label: "Waiting",
      value: s.waiting ?? "—",
      hint: "Patients marked present today",
      icon: faUserGroup,
      variant: "ocean",
    },
    {
      label: "Seen",
      value: s.done ?? "—",
      hint: "Appointments with a visit started today",
      icon: faClipboardCheck,
      variant: "violet",
    },
    {
      label: "Upcoming",
      value: s.upcoming ?? "—",
      hint: "Pending appointments today",
      icon: faCalendarDays,
      variant: "amber",
    },
  ];

  const showListEmpty = !isListLoading && todayAppointments.length === 0;

  return (
    <>
      {/* Header */}
      <div className="home-panel">
        <div className="home-panel__head home-panel__head--row">
          <div>
            <h2 className="home-panel__title mb-0">
              {selectedDoctor?.name ? `Assisting Dr. ${selectedDoctor.name}` : "Assistant console"}
            </h2>
            <span className="home-panel__desc">
              {selectedRole?.location?.name || "—"}
              {formatOfficeLocationLine(selectedRole?.location)
                ? ` · ${formatOfficeLocationLine(selectedRole.location)}`
                : ""}
              {" · switch the active doctor from the top bar"}
            </span>
          </div>
          <button
            type="button"
            className="btn home-dashboard__filter-btn"
            onClick={() => setShow(true)}
            disabled={!actingDoctorId}
          >
            <FontAwesomeIcon icon={faUserPlus} /> New appointment
          </button>
        </div>
      </div>

      {parentDoctors.length === 0 ? (
        <div className="home-panel home-dashboard__section-gap">
          <div className="home-panel__body">
            <p className="home-panel__empty mb-0">
              You have not been assigned to any doctor yet. Ask an admin to tag you to a doctor.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Today KPI tiles */}
          {isTodaySummaryLoading ? (
            <div className="home-dashboard__section-gap">
              <DashboardStatCardsSkeleton count={4} />
            </div>
          ) : (
            <div className="row g-3 home-stat-grid home-dashboard__section-gap">
              {tiles.map((item) => (
                <div className="col-xl-3 col-lg-6 col-md-6" key={item.label}>
                  <Tooltip title={item.hint} placement="top">
                    <div className={`home-stat-card home-stat-card--${item.variant} h-100`}>
                      <span className="home-stat-card__icon-wrap" aria-hidden="true">
                        <FontAwesomeIcon icon={item.icon} className="home-stat-card__icon" />
                      </span>
                      <div className="home-stat-card__body">
                        <span className="home-stat-card__label">{item.label}</span>
                        <span className="home-stat-card__value">{item.value}</span>
                      </div>
                    </div>
                  </Tooltip>
                </div>
              ))}
            </div>
          )}

          {/* Today's appointment list */}
          <div className="home-panel home-panel--table home-dashboard__section-gap">
            <div className="home-panel__head home-panel__head--row">
              <h2 className="home-panel__title mb-0">Today's appointments</h2>
              <span className="home-panel__desc">{today}</span>
            </div>
            <div className="home-panel__body home-panel__body--flush">
              <div className="recent-appts-wrap">
                <table className="recent-appts">
                  <thead>
                    <tr>
                      <th>Serial</th>
                      <th>Patient</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th aria-label="Actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {isListLoading && (
                      <tr className="recent-appts__msg-row">
                        <td colSpan={5}>Loading…</td>
                      </tr>
                    )}
                    {showListEmpty && (
                      <tr className="recent-appts__msg-row">
                        <td colSpan={5}>No appointments today for this doctor.</td>
                      </tr>
                    )}
                    {!isListLoading &&
                      todayAppointments.map((appointment) => {
                        const status = appointment?.status || "pending";
                        const isOpen = appointment?._id === selectedRow;
                        return (
                          <tr key={appointment?._id} className={isOpen ? "is-open" : ""}>
                            <td>{appointment?.serial ?? "—"}</td>
                            <td>
                              <span className="recent-appts__pat">
                                <span
                                  className="recent-appts__avatar"
                                  style={{ background: avatarColor(appointment?.patientName) }}
                                  aria-hidden="true"
                                >
                                  {initials(appointment?.patientName)}
                                </span>
                                <span className="recent-appts__pat-text">
                                  <span className="recent-appts__pat-name">
                                    {appointment?.patientName || "—"}
                                  </span>
                                  {appointment?.patientGender && (
                                    <small className="recent-appts__pat-sub">
                                      {titleCase(appointment.patientGender)}
                                    </small>
                                  )}
                                </span>
                              </span>
                            </td>
                            <td>{appointment?.patientPhone || "—"}</td>
                            <td>
                              <span
                                className="recent-appts__status-wrap"
                                ref={(el) => (ref.current[appointment?._id] = el)}
                              >
                                <button
                                  type="button"
                                  className={`recent-appts__status recent-appts__status--${status}`}
                                  onClick={() => setSelectedRow(isOpen ? null : appointment?._id)}
                                  aria-haspopup="menu"
                                  aria-expanded={isOpen}
                                >
                                  <span className="recent-appts__status-dot" aria-hidden="true" />
                                  {titleCase(status)}
                                  <FontAwesomeIcon
                                    size="xs"
                                    icon={faEllipsisVertical}
                                    className="recent-appts__status-caret"
                                  />
                                </button>
                                {isOpen && (
                                  <div className="recent-appts__menu" role="menu">
                                    <button
                                      type="button"
                                      className="recent-appts__menu-item"
                                      onClick={() => setStatusFun("Present", appointment)}
                                      role="menuitem"
                                    >
                                      Present
                                    </button>
                                    <button
                                      type="button"
                                      className="recent-appts__menu-item"
                                      onClick={() => setStatusFun("Pending", appointment)}
                                      role="menuitem"
                                    >
                                      Pending
                                    </button>
                                  </div>
                                )}
                              </span>
                            </td>
                            <td className="recent-appts__action-cell">
                              <button
                                type="button"
                                className="recent-appts__view"
                                onClick={() => createVisitAndNavigate(appointment)}
                              >
                                <FontAwesomeIcon size="sm" icon={faEye} />
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* My doctors */}
          <div className="home-panel home-dashboard__section-gap">
            <div className="home-panel__head home-panel__head--row">
              <h2 className="home-panel__title mb-0">My doctors</h2>
              <span className="home-panel__desc">Doctors you are assigned to</span>
            </div>
            <div className="home-panel__body">
              <div className="row g-3">
                {parentDoctors.map((doctor) => {
                  const isActive = selectedDoctor?._id === doctor?._id;
                  return (
                    <div className="col-xl-3 col-lg-4 col-md-6" key={doctor._id}>
                      <div
                        className="home-doctor-card"
                        style={isActive ? { borderColor: "#0573f5" } : undefined}
                      >
                        <span className="home-doctor-card__avatar" aria-hidden="true">
                          {(doctor.name || "?").trim().charAt(0).toUpperCase()}
                        </span>
                        <div className="home-doctor-card__body">
                          <span className="home-doctor-card__name">{doctor.name || "—"}</span>
                          {doctor.email && (
                            <span className="home-doctor-card__email">{doctor.email}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      <CustomModal
        customClass="appointment"
        size="xl"
        show={show}
        setShow={setShow}
        customSize={"size-72"}
      >
        <CreateAppointment setShow={setShow} refresh={refreshAll} />
      </CustomModal>
    </>
  );
};

export default AssistantTodayConsole;
